import type { Sheet } from "@fortune-sheet/core";
import type { Worksheet } from "exceljs";

/**
 * The print setup a workbook declares, in the units a PDF renderer works in. The xlsx and the PDF are two
 * renderings of ONE sheet, and they only agree on paper size, margins, repeated header rows and page count
 * if the PDF reads them off the sheet. `excelBufferToFortuneSheet` attaches one to every sheet it converts.
 */

/** Excel margins are in inches; jsPDF (and every other paper measurement here) works in points. */
const INCHES_TO_POINTS = 72;

/** Excel's `paperSize` codes for the two sizes this app actually prints on; the full enum has ~120, and an
 *  unmapped code is reported as `null` dimensions rather than quietly treated as one of these. */
export const PAPER_SIZE_LETTER = 1;
export const PAPER_SIZE_A4 = 9;

/** Portrait dimensions of a sheet of paper, in points. */
export type PaperDimensionsPt = { widthPt: number; heightPt: number };

/** A4 is 210 x 297 mm; US Letter is 8.5 x 11 in. Both expressed at 72pt to the inch. */
const PAPER_DIMENSIONS_PT: Record<number, PaperDimensionsPt> = {
	[PAPER_SIZE_A4]: { widthPt: 595.28, heightPt: 841.89 },
	[PAPER_SIZE_LETTER]: { widthPt: 612, heightPt: 792 },
};

/** Physical size of an Excel `paperSize` code, or `null` for one we don't know. `null` covers both an
 *  absent `paperSize` and an unmapped code and cannot tell them apart; `resolvePaperPt` silently prints
 *  both on Letter, so mapping a new size here is the only way to stop one printing on Letter. */
export const paperDimensionsForCode = (code: number | undefined): PaperDimensionsPt | null => (code === undefined ? null : (PAPER_DIMENSIONS_PT[code] ?? null));

export type PageMarginsPt = {
	left: number;
	right: number;
	top: number;
	bottom: number;
	header: number;
	footer: number;
};

/** Zero-based, inclusive row range repeated at the top of every printed page. */
export type PrintTitleRows = { startRow: number; endRow: number };

export type SheetPageSetup = {
	/** The raw Excel code, forwarded verbatim. Every renderer uses `paperDimensionsPt` instead, so an
	 *  unmapped code is nowhere reported. */
	paperSizeCode: number | undefined;
	/** `null` when `paperSizeCode` is absent or unmapped; the renderer prints Letter for both, see above. */
	paperDimensionsPt: PaperDimensionsPt | null;
	orientation: "portrait" | "landscape" | undefined;
	marginsPt: PageMarginsPt | undefined;
	/** Parsed from Excel's `"1:4"` range string; `undefined` when the sheet repeats no rows. */
	printTitleRows: PrintTitleRows | undefined;
	/** Excel's `printArea` range string, unparsed. NOT APPLIED: `downloadDayViewPdf` prints the sheet's
	 *  whole used range, and the two workbooks that set one set it TO their used range. Read this field
	 *  before assuming the PDF respects it. */
	printArea: string | undefined;
	horizontalCentered: boolean | undefined;
	/** NOT APPLIED, unlike `horizontalCentered`: Excel centres the printed rows in the leftover height of
	 *  the LAST page only, which needs the pagination before drawing, and the renderer paginates as it
	 *  draws. No workbook here asks for it. */
	verticalCentered: boolean | undefined;
	fitToPage: boolean | undefined;
	fitToWidth: number | undefined;
	fitToHeight: number | undefined;
	/** Excel's manual scale percentage (100 = unscaled), meaningful only when `fitToPage` is off. NOT
	 *  APPLIED: every workbook here fits to page instead, and ExcelJS materialises `100` on a sheet that
	 *  declares nothing. A sheet carrying a real one will print at 100% until this is honoured. */
	scale: number | undefined;
	oddHeader: string | undefined;
	oddFooter: string | undefined;
};

/** A converted Fortune-Sheet sheet, carrying the print setup of the worksheet it came from. */
export type SheetWithPageSetup = Sheet & { pageSetup: SheetPageSetup };

/** `"1:4"`, or `"$1:$4"` as some writers spell it. Anything else is a malformed range. */
const PRINT_TITLES_ROW_PATTERN = /^\$?(\d+):\$?(\d+)$/;

/** Excel's one-based, inclusive `printTitlesRow` range as zero-based row indices. Throws on a present-but-
 *  unparseable value rather than dropping it: every workbook that sets this is one of ours, and printing
 *  without repeated headers is the failure clients report as "the second page has no column titles". */
export const parsePrintTitlesRow = (range: string | undefined): PrintTitleRows | undefined => {
	if (range === undefined) return undefined;

	const match = PRINT_TITLES_ROW_PATTERN.exec(range.trim());
	if (!match) throw new Error(`Unparseable Excel printTitlesRow range: "${range}"`);

	const startRow = Number.parseInt(match[1], 10) - 1;
	const endRow = Number.parseInt(match[2], 10) - 1;
	if (startRow < 0 || endRow < startRow) throw new Error(`Out-of-order Excel printTitlesRow range: "${range}"`);

	return { startRow, endRow };
};

const marginsToPoints = (margins: Worksheet["pageSetup"]["margins"]): PageMarginsPt | undefined => {
	if (!margins) return undefined;
	return {
		left: margins.left * INCHES_TO_POINTS,
		right: margins.right * INCHES_TO_POINTS,
		top: margins.top * INCHES_TO_POINTS,
		bottom: margins.bottom * INCHES_TO_POINTS,
		header: margins.header * INCHES_TO_POINTS,
		footer: margins.footer * INCHES_TO_POINTS,
	};
};

/** Read a worksheet's print setup into renderer units. ExcelJS materialises Excel's own defaults while
 *  parsing a sheet that declares none, so what comes out is the effective setup Excel itself would print
 *  with — not a guess. Fields Excel genuinely leaves unset stay `undefined`. */
export const extractPageSetup = (worksheet: Worksheet): SheetPageSetup => {
	const pageSetup = worksheet.pageSetup;
	// Optional-chained despite the non-nullable type: ExcelJS types this as an object but leaves it
	// literally `null` on a parsed sheet that carries no header/footer.
	const headerFooter = worksheet.headerFooter;

	return {
		paperSizeCode: pageSetup.paperSize,
		paperDimensionsPt: paperDimensionsForCode(pageSetup.paperSize),
		orientation: pageSetup.orientation,
		marginsPt: marginsToPoints(pageSetup.margins),
		printTitleRows: parsePrintTitlesRow(pageSetup.printTitlesRow),
		printArea: pageSetup.printArea,
		horizontalCentered: pageSetup.horizontalCentered,
		verticalCentered: pageSetup.verticalCentered,
		fitToPage: pageSetup.fitToPage,
		fitToWidth: pageSetup.fitToWidth,
		fitToHeight: pageSetup.fitToHeight,
		scale: pageSetup.scale,
		oddHeader: headerFooter?.oddHeader,
		oddFooter: headerFooter?.oddFooter,
	};
};
